import dayjs from "dayjs";
import { StickyNote } from "lucide-react";
import {
  BonificoOrderDTO,
  BonificoOrderStatus,
  b2bAmountPaidInCents,
  b2bCompanyNameOf,
  externalSettlementTitle,
  purchasedItemSnapshotOf,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { SettlementBadge } from "./SettlementBadge.tsx";
import {
  B2B_ORDERS_PRODUCT_FILTER,
  BonificoOrderSortField,
  BonificoOrdersFilters,
  BonificoOrdersListQuery,
  pickBonificoOrdersFilters,
  SortDirection,
} from "../../ProductService/BonificoOrder/BonificoOrdersListQuery.ts";
import { usePurchasedItemNames } from "../../ProductService/PurchasedItem/usePurchasedItemNames.ts";
import { CopyableText } from "../../UI/CopyableText.tsx";
import { TableColumnHeader } from "../../UI/Table/TableColumnHeader.tsx";
import { DateRangeFilter } from "../../UI/Table/Filters/DateRangeFilter.tsx";
import { AmountRangeFilter } from "../../UI/Table/Filters/AmountRangeFilter.tsx";
import { BonificoOrderStatusBadge } from "./BonificoOrderStatusBadge.tsx";
import {
  BonificoOrderActionHandlers,
  BonificoOrderActions,
} from "./BonificoOrderActions.tsx";
import { CheckboxListFilter } from "../../UI/Table/Filters/CheckboxListFilter.tsx";
import { PurchasedItemFilter } from "../OrderFilters/PurchasedItemFilter.tsx";

const DATE_SORT_LABELS = { asc: "Dal meno recente", desc: "Dal più recente" };

const B2B_PRODUCT_FILTER_OPTIONS = [
  { id: B2B_ORDERS_PRODUCT_FILTER, label: "Ordini B2B" },
];

const STATUS_FILTER_OPTIONS = Object.values(BonificoOrderStatus).map(
  (status) => ({
    value: status,
    label: <BonificoOrderStatusBadge status={status} />,
  }),
);

function formatDate(dateUtc?: string | null): string {
  return dateUtc ? dayjs(dateUtc).format("DD/MM/YYYY") : "—";
}

function CustomerName({ order }: { order: BonificoOrderDTO }) {
  const customerFullName = `${order.customer.lastName} ${order.customer.firstName}`;
  const companyName = b2bCompanyNameOf(order);
  if (!companyName) return <span className="truncate">{customerFullName}</span>;
  return (
    <span className="truncate" title={`Referente: ${customerFullName}`}>
      {companyName}
    </span>
  );
}

export function BonificoOrdersTable({
  orders,
  query,
  onSort,
  onFiltersChange,
  onSelectOrder,
  actionHandlers,
  actionsDisabled,
}: {
  orders: BonificoOrderDTO[];
  query: BonificoOrdersListQuery;
  onSort: (field: BonificoOrderSortField, direction: SortDirection) => void;
  onFiltersChange: (filters: BonificoOrdersFilters) => void;
  onSelectOrder: (order: BonificoOrderDTO) => void;
  actionHandlers: BonificoOrderActionHandlers;
  actionsDisabled: boolean;
}) {
  const { resolveName } = usePurchasedItemNames();
  const filters = pickBonificoOrdersFilters(query);
  const updateFilters = (changes: BonificoOrdersFilters) =>
    onFiltersChange({ ...filters, ...changes });
  const sortProps = (field: BonificoOrderSortField) => ({
    sortDirection: query.sortBy === field ? query.sortDirection : undefined,
    onSort: (direction: SortDirection) => onSort(field, direction),
  });

  return (
    <div className="overflow-x-auto max-w-full">
      <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th className="pl-6 pr-2 py-3">
              <TableColumnHeader
                label="Data creazione"
                {...sortProps("createdAt")}
                sortLabels={DATE_SORT_LABELS}
                isFiltered={!!(filters.createdFrom || filters.createdTo)}
                renderFilter={(close) => (
                  <DateRangeFilter
                    value={{ from: filters.createdFrom, to: filters.createdTo }}
                    onApply={({ from, to }) =>
                      updateFilters({ createdFrom: from, createdTo: to })
                    }
                    close={close}
                  />
                )}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Cliente"
                {...sortProps("customer")}
                sortLabels={{ asc: "A → Z", desc: "Z → A" }}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Prodotto"
                isPanelWide
                isFiltered={!!filters.productIds?.length}
                renderFilter={(close) => (
                  <PurchasedItemFilter
                    value={filters.productIds ?? []}
                    onApply={(productIds) => updateFilters({ productIds })}
                    close={close}
                    extraOptions={B2B_PRODUCT_FILTER_OPTIONS}
                  />
                )}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Importo"
                {...sortProps("amount")}
                sortLabels={{ asc: "Dal più basso", desc: "Dal più alto" }}
                isFiltered={
                  filters.amountMinInCents !== undefined ||
                  filters.amountMaxInCents !== undefined
                }
                renderFilter={(close) => (
                  <AmountRangeFilter
                    value={{
                      minInCents: filters.amountMinInCents,
                      maxInCents: filters.amountMaxInCents,
                    }}
                    onApply={({ minInCents, maxInCents }) =>
                      updateFilters({
                        amountMinInCents: minInCents,
                        amountMaxInCents: maxInCents,
                      })
                    }
                    close={close}
                  />
                )}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Stato"
                isFiltered={!!filters.statuses?.length}
                renderFilter={(close) => (
                  <CheckboxListFilter
                    options={STATUS_FILTER_OPTIONS}
                    value={filters.statuses ?? []}
                    onApply={(statuses) => updateFilters({ statuses })}
                    close={close}
                  />
                )}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Data contabile"
                {...sortProps("receiptDate")}
                sortLabels={DATE_SORT_LABELS}
                isFiltered={
                  !!(
                    filters.receiptFrom ||
                    filters.receiptTo ||
                    filters.receiptMissing
                  )
                }
                renderFilter={(close) => (
                  <DateRangeFilter
                    value={{
                      from: filters.receiptFrom,
                      to: filters.receiptTo,
                      onlyMissing: filters.receiptMissing,
                    }}
                    missingLabel="Solo senza data contabile"
                    onApply={({ from, to, onlyMissing }) =>
                      updateFilters({
                        receiptFrom: from,
                        receiptTo: to,
                        receiptMissing: onlyMissing,
                      })
                    }
                    close={close}
                  />
                )}
              />
            </th>
            <th className="px-2 py-3">
              <TableColumnHeader
                label="Data valuta"
                {...sortProps("valueDate")}
                sortLabels={DATE_SORT_LABELS}
                isFiltered={
                  !!(
                    filters.valueFrom ||
                    filters.valueTo ||
                    filters.valueMissing
                  )
                }
                renderFilter={(close) => (
                  <DateRangeFilter
                    value={{
                      from: filters.valueFrom,
                      to: filters.valueTo,
                      onlyMissing: filters.valueMissing,
                    }}
                    missingLabel="Solo senza data valuta"
                    onApply={({ from, to, onlyMissing }) =>
                      updateFilters({
                        valueFrom: from,
                        valueTo: to,
                        valueMissing: onlyMissing,
                      })
                    }
                    close={close}
                  />
                )}
              />
            </th>
            <th className="w-px pl-2 pr-4 py-3">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr className="bg-white">
              <td colSpan={8} className="px-2 py-10 text-center text-gray-500">
                Nessun ordine trovato.
              </td>
            </tr>
          )}
          {orders.map((order) => {
            const productName =
              order.normasteachingProductId && order.productType
                ? resolveName({
                    id: order.normasteachingProductId,
                    type: order.productType,
                    snapshot: purchasedItemSnapshotOf(order),
                  }).name
                : undefined;
            return (
              <tr
                key={order.orderId}
                className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600"
                onClick={() => onSelectOrder(order)}
              >
                <td className="pl-6 pr-2 py-3 whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {dayjs(order.createdAt).format("DD/MM/YYYY")}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">
                      {dayjs(order.createdAt).format("HH:mm:ss")}
                    </span>
                  </div>
                </td>
                <td className="px-2 py-3 max-w-[160px]">
                  <div className="flex items-center gap-1 font-medium text-gray-900 dark:text-white">
                    <CustomerName order={order} />
                    {order.notes && (
                      <span title={order.notes} className="shrink-0">
                        <StickyNote
                          aria-label="Ordine con nota"
                          className="w-3.5 h-3.5 text-amber-500"
                        />
                      </span>
                    )}
                  </div>
                  <div
                    className="text-gray-500 dark:text-gray-400 text-xs truncate"
                    title={order.customer.email}
                  >
                    {order.customer.email}
                  </div>
                </td>
                <td className="px-2 py-3 max-w-[120px] truncate">
                  {order.b2bContract ? (
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900 dark:text-white">
                        Contratto B2B
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        del{" "}
                        {dayjs(order.b2bContract.contractDateUtc).format(
                          "DD/MM/YYYY",
                        )}
                      </span>
                    </div>
                  ) : order.normasteachingProductId && productName ? (
                    <CopyableText
                      className="cursor-pointer hover:text-blue-600"
                      title={productName}
                      value={order.normasteachingProductId}
                      copyMessage={`ID del ${order.productType === "bundle" ? "bundle" : "prodotto"} copiato negli appunti!`}
                    >
                      {productName}
                    </CopyableText>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-2 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {order.amountPaidInCents
                    ? `€${(order.amountPaidInCents.amount / 100).toFixed(2)}`
                    : "—"}
                  {order.status === BonificoOrderStatus.PartiallyPaid &&
                    order.b2bContract && (
                      <div className="text-xs font-normal text-gray-500 dark:text-gray-400">
                        Pagati €
                        {(
                          b2bAmountPaidInCents(order.b2bContract) / 100
                        ).toFixed(2)}
                      </div>
                    )}
                </td>
                <td className="px-2 py-3">
                  <div className="flex flex-col items-start gap-1">
                    <BonificoOrderStatusBadge status={order.status} />
                    {order.installmentsSettlement && <SettlementBadge />}
                    {order.externalSettlement && (
                      <SettlementBadge
                        label={externalSettlementTitle(
                          order.externalSettlement,
                        )}
                      />
                    )}
                  </div>
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  {formatDate(order.receiptDateUtc)}
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  {formatDate(order.valueDateUtc)}
                </td>
                <td
                  className="w-px pl-2 pr-4 py-3 whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <BonificoOrderActions
                    order={order}
                    handlers={actionHandlers}
                    disabled={actionsDisabled}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
